import { HttpClient } from "@angular/common/http";
import { computed, inject, Injectable, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { ApiResponse } from "app/@core/types";
import { DayjsService } from "app/@core/utils/dayjs.service";
import { environment } from "environments/environment";
import { map, of } from "rxjs";
import { Event } from "../types";
import { unwrapResponse } from "../utils/api-utils";

@Injectable({ providedIn: "root" })
export class EventService {
  private http = inject(HttpClient);
  private dayjs = inject(DayjsService);
  private readonly baseUrl = `${environment.baseUrl}courses/event/`;

  courseId = signal<number | null>(null);

  eventsResource = rxResource({
    request: () => this.courseId(),
    loader: ({ request: courseId }) => {
      if (!courseId) return of([]);
      return this.http
        .get<ApiResponse>(`${this.baseUrl}${courseId}/all`)
        .pipe(map(unwrapResponse<Event[]>));
    },
  });

  readonly allEvents = computed(() => this.eventsResource.value() ?? []);

  readonly upcomingEvents = computed(() =>
    this.allEvents().filter((event) => this.dayjs.isAfter(event.datetime)),
  );

  readonly pastEvents = computed(() =>
    this.allEvents().filter((event) => this.dayjs.isBefore(event.datetime)),
  );

  readonly pastRecordings = computed(() =>
    this.allEvents()
      .filter((e) => this.filterPastRecording(e))
      .sort((a, b) => this.dayjs.compareDesc(b.datetime, a.datetime)),
  );

  private filterPastRecording(event: Event): boolean {
    const video = event.options?.video;
    if (typeof video === "object" && video !== null) {
      return this.dayjs.isBefore(event.datetime) && !!video.id;
    }
    return false;
  }

  // Methods to get the course events
  loadCourseEvents(id: number) {
    this.courseId.set(id);
  }

  getModuleEvents(moduleId: number) {
    return computed(() =>
      this.allEvents().filter((event) => event.module_id === moduleId),
    );
  }

  getEventById(id: number) {
    return computed(() => this.allEvents().find((event) => event.id === id));
  }

  unload(): void {
    this.courseId.set(null);
  }

  // Reload just the current data
  refresh(): void {
    this.eventsResource.reload();
  }
}
